import { useEffect, useState } from "react";
import { api, currency } from "@/lib/api";
import { PRESETS, getRange } from "@/lib/dates";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ShoppingCart,
  FileText,
  CheckCircle2,
  Clock,
  TrendingUp,
  PoundSterling,
  Wallet,
  AlertCircle,
  Receipt,
  Percent,
} from "lucide-react";

const Metric = ({ label, value, icon: Icon, accent, testid, breakdown }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-6 flex flex-col gap-3" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-sm text-[#5B5C5F] uppercase tracking-wider">{label}</span>
      <span
        className="w-9 h-9 rounded-md flex items-center justify-center"
        style={{ backgroundColor: `${accent}1a`, color: accent }}
      >
        <Icon className="w-5 h-5" />
      </span>
    </div>
    <span className="font-heading text-3xl font-bold" style={{ color: accent }}>
      {value}
    </span>
    {breakdown && breakdown.length > 0 && (
      <div className="text-xs text-[#5B5C5F] space-y-0.5 border-t border-slate-100 pt-2">
        {breakdown.map((b, i) => (
          <div key={i} className="flex justify-between">
            <span>{b.label}</span>
            <span className="font-mono-num" style={b.color ? { color: b.color } : undefined}>
              {b.value}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [preset, setPreset] = useState("all");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const fetchData = (params) => {
    api.get("/dashboard", { params }).then(({ data }) => setData(data));
  };

  useEffect(() => {
    if (preset === "custom") {
      if (customStart && customEnd) fetchData({ start: customStart, end: customEnd });
      return;
    }
    const { start, end } = getRange(preset);
    fetchData(start ? { start, end } : {});
  }, [preset, customStart, customEnd]);

  const d = data || {};
  const g = (k) => d[k] || 0;

  return (
    <div className="max-w-6xl mx-auto" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#032352]">Dashboard</h1>
          <p className="text-[#5B5C5F] mt-1">Overview of orders, revenue, VAT and collections.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={preset} onValueChange={setPreset}>
            <SelectTrigger className="w-48" data-testid="dashboard-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRESETS.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {preset === "custom" && (
            <>
              <Input
                type="date"
                data-testid="dashboard-custom-start"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="w-40"
              />
              <Input
                type="date"
                data-testid="dashboard-custom-end"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="w-40"
              />
            </>
          )}
        </div>
      </div>

      {/* Orders summary */}
      <div className="bg-[#032352] text-white rounded-lg p-6 mb-6" data-testid="orders-summary">
        <div className="flex items-center gap-3">
          <ShoppingCart className="w-6 h-6 text-white/80" />
          <span className="text-sm uppercase tracking-wider text-white/80">Total Orders</span>
        </div>
        <p className="font-heading text-5xl font-extrabold mt-2" data-testid="total-orders">
          {g("total_orders")}
        </p>
        <div className="grid grid-cols-4 gap-4 mt-6">
          <div className="border-t border-white/20 pt-3">
            <div className="flex items-center gap-2 text-white/70 text-xs uppercase tracking-wider">
              <FileText className="w-4 h-4" /> Generated
            </div>
            <p className="font-mono-num text-2xl font-semibold mt-1" data-testid="invoices-generated">
              {g("invoices_generated")}
            </p>
          </div>
          <div className="border-t border-emerald-400/50 pt-3">
            <div className="flex items-center gap-2 text-emerald-300 text-xs uppercase tracking-wider">
              <CheckCircle2 className="w-4 h-4" /> Paid
            </div>
            <p className="font-mono-num text-2xl font-semibold mt-1" data-testid="paid-count">
              {g("paid_count")}
            </p>
          </div>
          <div className="border-t border-amber-400/50 pt-3">
            <div className="flex items-center gap-2 text-amber-300 text-xs uppercase tracking-wider">
              <Clock className="w-4 h-4" /> Outstanding
            </div>
            <p className="font-mono-num text-2xl font-semibold mt-1" data-testid="outstanding-count">
              {g("outstanding_count")}
            </p>
          </div>
          <div className="border-t border-red-400/50 pt-3">
            <div className="flex items-center gap-2 text-red-300 text-xs uppercase tracking-wider">
              <AlertCircle className="w-4 h-4" /> Overdue
            </div>
            <p className="font-mono-num text-2xl font-semibold mt-1 text-red-300" data-testid="overdue-count">
              {g("overdue_count")}
            </p>
          </div>
        </div>
      </div>

      {/* Financial metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-4">
        <Metric
          label="Revenue"
          value={currency(g("total_revenue_gross"))}
          icon={PoundSterling}
          accent="#032352"
          testid="metric-revenue"
          breakdown={[
            { label: "Ex VAT", value: currency(g("total_revenue")) },
            { label: "VAT", value: currency(g("total_revenue_vat")) },
          ]}
        />
        <Metric
          label="Profit"
          value={currency(g("total_revenue") - g("total_expenses"))}
          icon={TrendingUp}
          accent="#10B981"
          testid="metric-profit"
          breakdown={[
            { label: "Revenue ex VAT", value: currency(g("total_revenue")) },
            { label: "− Expenses", value: currency(g("total_expenses")), color: "#EF4444" },
          ]}
        />
        <Metric
          label="Overdue"
          value={currency(g("total_overdue_gross"))}
          icon={AlertCircle}
          accent="#EF4444"
          testid="metric-overdue"
          breakdown={[
            { label: "Ex VAT", value: currency(g("total_overdue")) },
            { label: "VAT", value: currency(g("total_overdue_vat")) },
            { label: `${g("overdue_count")} invoice${g("overdue_count") !== 1 ? "s" : ""} past due`, value: "" },
          ]}
        />
        <Metric
          label="Outstanding"
          value={currency(g("total_outstanding_gross"))}
          icon={AlertCircle}
          accent="#F59E0B"
          testid="metric-outstanding"
          breakdown={[
            { label: "Ex VAT", value: currency(g("total_outstanding")) },
            { label: "VAT", value: currency(g("total_outstanding_vat")) },
          ]}
        />
        <Metric
          label="Collected"
          value={currency(g("total_collected_gross"))}
          icon={Wallet}
          accent="#5B5C5F"
          testid="metric-collected"
          breakdown={[
            { label: "Ex VAT", value: currency(g("total_collected")) },
            { label: "VAT", value: currency(g("total_collected_vat")) },
          ]}
        />
      </div>

      {/* VAT + Expenses */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric
          label="Output VAT"
          value={currency(g("output_vat"))}
          icon={Percent}
          accent="#032352"
          testid="metric-output-vat"
          breakdown={[{ label: "VAT charged on sales", value: "" }]}
        />
        <Metric
          label="Input VAT"
          value={currency(g("input_vat"))}
          icon={Percent}
          accent="#5B5C5F"
          testid="metric-input-vat"
          breakdown={[{ label: "VAT on expenses", value: "" }]}
        />
        <Metric
          label="Net VAT"
          value={currency(g("net_vat"))}
          icon={Percent}
          accent="#EF4444"
          testid="metric-net-vat"
          breakdown={[{ label: "Output − Input VAT", value: "" }]}
        />
        <Metric
          label="Expenses"
          value={currency(g("total_expenses_gross") || g("total_expenses") + g("total_expenses_vat"))}
          icon={Receipt}
          accent="#EF4444"
          testid="metric-expenses"
          breakdown={[
            { label: "Ex VAT", value: currency(g("total_expenses")) },
            { label: "VAT", value: currency(g("total_expenses_vat")) },
          ]}
        />
      </div>

      {/* Profit & Loss Summary */}
      {(() => {
        const hasExpenses = g("total_expenses") > 0;
        const netProfit = g("total_revenue") - g("total_expenses");
        const PLRow = ({ label, sub, value, negative }) => (
          <div className="flex justify-between items-baseline py-2 border-b border-slate-100">
            <div>
              <span className="text-sm text-[#5B5C5F]">{label}</span>
              {sub && <span className="text-xs text-[#5B5C5F]/60 ml-1">({sub})</span>}
            </div>
            <span className={`font-mono-num text-sm font-medium ${negative ? "text-red-500" : "text-[#032352]"}`}>
              {value}
            </span>
          </div>
        );
        const PLTotal = ({ label, value, final }) => (
          <div className={`flex justify-between items-baseline py-2.5 mt-1 border-t-2 ${final ? "border-[#032352]" : "border-slate-300"}`}>
            <span className={`text-xs uppercase tracking-wider font-semibold ${final ? "text-[#032352]" : "text-[#5B5C5F]"}`}>{label}</span>
            <span className={`font-mono-num font-bold ${final ? "text-xl" : "text-sm"} ${final && netProfit < 0 ? "text-red-600" : "text-[#032352]"}`}>
              {value}
            </span>
          </div>
        );
        return (
          <div className="mt-4 bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="pl-summary">
            <div className="bg-[#032352] px-6 py-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-white/70" />
              <span className="text-white text-sm font-semibold uppercase tracking-wider">Profit &amp; Loss Summary</span>
              <span className="text-white/40 text-xs ml-auto italic">All figures ex-VAT unless stated</span>
            </div>
            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* P&L Statement */}
              <div>
                <p className="text-xs uppercase tracking-wider text-[#5B5C5F] mb-3 font-semibold">Income &amp; Costs</p>
                <PLRow label="Sales Revenue" sub="ex VAT" value={currency(g("total_revenue"))} />
                {hasExpenses && (
                  <PLRow label="Operating Expenses" sub="from Expenses tracker" value={`−${currency(g("total_expenses"))}`} negative />
                )}
                <PLTotal label="Net Profit" value={currency(netProfit)} final />
              </div>
              {/* VAT Position */}
              <div>
                <p className="text-xs uppercase tracking-wider text-[#5B5C5F] mb-3 font-semibold">VAT Position</p>
                <PLRow label="Output VAT" sub="charged on sales" value={currency(g("output_vat"))} />
                {g("input_vat") > 0 && (
                  <PLRow label="Input VAT" sub="reclaimable on expenses" value={`−${currency(g("input_vat"))}`} negative />
                )}
                <PLTotal label="Net VAT Due to HMRC" value={currency(g("net_vat"))} final />
                <div className="mt-6 bg-slate-50 rounded-md p-4 border border-slate-100">
                  <p className="text-xs uppercase tracking-wider text-[#5B5C5F] mb-2 font-semibold">Cash Position</p>
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm text-[#5B5C5F]">Collected (received)</span>
                    <span className="font-mono-num text-sm font-medium text-emerald-600">{currency(g("total_collected_gross"))}</span>
                  </div>
                  <div className="flex justify-between items-baseline mt-1">
                    <span className="text-sm text-[#5B5C5F]">Outstanding (owed)</span>
                    <span className="font-mono-num text-sm font-medium text-amber-600">{currency(g("total_outstanding_gross"))}</span>
                  </div>
                  <div className="flex justify-between items-baseline mt-1">
                    <span className="text-sm text-[#5B5C5F]">Overdue (at risk)</span>
                    <span className="font-mono-num text-sm font-medium text-red-500">{currency(g("total_overdue_gross"))}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
